/**
 * Step 2: Configuration
 *
 * Collects compression codec, schema reference, and source selection
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDatasetConfiguration } from '@/features/datasets/store/datasetCreationStore';
import { useSources } from '@/hooks/useSources';
import { useSchemas } from '@/features/schemas/hooks/useSchemas';
import { COMPRESSION_CODECS } from '@/features/datasets/types/dataset.types';
import type { CompressionCodec } from '@/features/datasets/types/dataset.types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';

/**
 * Configuration Step Component
 */
export const DatasetConfigurationStep: React.FC = () => {
  const {
    compression,
    schemaId,
    sourceId,
    setCompression,
    setSchemaId,
    setSourceId,
  } = useDatasetConfiguration();

  // Fetch sources and schemas
  const { data: sourcesData, isLoading: isLoadingSources, error: sourcesError } = useSources({ limit: 1000 });
  const { data: schemasData, isLoading: isLoadingSchemas, error: schemasError } = useSchemas({ size: 1000 });

  const sources = sourcesData?.items || [];
  const schemas = schemasData?.items || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Configuration</h2>
        <p className="text-muted-foreground text-sm">
          Configure storage settings and optional references.
        </p>
      </div>

      {/* Compression Codec */}
      <div className="space-y-2">
        <Label htmlFor="dataset-compression" className="required">
          Compression
        </Label>
        <Select
          value={compression}
          onValueChange={(value) => setCompression(value as CompressionCodec)}
        >
          <SelectTrigger id="dataset-compression">
            <SelectValue placeholder="Select compression" />
          </SelectTrigger>
          <SelectContent>
            {COMPRESSION_CODECS.map((codec) => (
              <SelectItem key={codec.value} value={codec.value}>
                <div className="flex flex-col items-start">
                  <span className="font-medium">{codec.label}</span>
                  <span className="text-xs text-muted-foreground">{codec.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Choose compression algorithm for storage optimization
        </p>
      </div>

      {/* Source Selection */}
      <div className="space-y-2">
        <Label htmlFor="dataset-source">
          Source
        </Label>
        {isLoadingSources ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading sources...</span>
          </div>
        ) : sourcesError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load sources: {sourcesError instanceof Error ? sourcesError.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Select
              value={sourceId || 'none'}
              onValueChange={(value) => setSourceId(value === 'none' ? null : value)}
            >
              <SelectTrigger id="dataset-source">
                <SelectValue placeholder="Select a source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground italic">None</span>
                </SelectItem>
                {sources
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((source) => (
                    <SelectItem key={source.source_id} value={source.source_id}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{source.name}</span>
                        {source.description && (
                          <span className="text-xs text-muted-foreground">{source.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Optional: Link this dataset to a data source ({sources.length} available)
            </p>
          </>
        )}
      </div>

      {/* Schema Reference */}
      <div className="space-y-2">
        <Label htmlFor="dataset-schema">
          Schema Reference
        </Label>
        {isLoadingSchemas ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading schemas...</span>
          </div>
        ) : schemasError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load schemas: {schemasError instanceof Error ? schemasError.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Select
              value={schemaId || 'none'}
              onValueChange={(value) => setSchemaId(value === 'none' ? null : value)}
            >
              <SelectTrigger id="dataset-schema">
                <SelectValue placeholder="Select a schema" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground italic">None</span>
                </SelectItem>
                {schemas
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((schema) => (
                    <SelectItem key={schema.id} value={schema.id}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{schema.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {schema.field_count} fields
                        </span>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Optional: Link this dataset to an existing schema ({schemas.length} available)
            </p>
          </>
        )}
      </div>
    </div>
  );
};
